import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { ArrowForward } from '@mui/icons-material'
import { Button, FormControlLabel, Stack, Switch, Typography } from '@mui/material'
import { ThreeEvent } from '@react-three/fiber'
import { useMutation } from '@tanstack/react-query'
import { ImportButton } from '@ui/actions'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore } from '@editorStores'
import { useModelClickListeners, useSelectionMode, useTypeInteraction } from '@editorHooks'
import { useModelInvalidation } from '@structuralPlanningHooks'
import { IFCOpeningParseReport, startIfcOpeningParse } from '@mutations'
import { getOpeningParseWarning } from './ifcParseReport'

interface Props {
  guids: string[]
  ifcFileId: string | null
  projectId: string
  onSuccess: () => void
}

const SELECT_WALL_FOR_OPENING_MODE_KEY = 'select-wall-for-ifc-opening'

const wallTypeInteraction: ElementTypes[] = ['inner_walls', 'outer_walls']

const IFCParseAsWallOpeningForm = ({
  guids,
  ifcFileId,
  projectId,
  onSuccess,
}: Props): ReactElement => {
  const invalidateModel = useModelInvalidation()
  const walls = useModelStore(state => state.model.walls)
  const { setSelectionMode, unsetSelectionMode, selectionModeKey } = useSelectionMode()
  const [wallGuidForOpening, setWallGuidForOpening] = useState<string>('')
  const [limitOpeningXYCoords, setLimitOpeningXYCoords] = useState(true)
  /** When single guid: editable opening ID; when multiple: not shown, use guids as-is. */
  const [ifcOpeningIds, setIfcOpeningIds] = useState<string[]>(guids)
  const isSelectingWall = selectionModeKey === SELECT_WALL_FOR_OPENING_MODE_KEY

  const wallGuids = useMemo(() => new Set(walls.map(wall => wall.guid)), [walls])

  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    setIfcOpeningIds(guids)
  }, [guids])

  useTypeInteraction(wallTypeInteraction, isSelectingWall)

  const handleWallSelect = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      const wallGuid = event.object.name

      if (!wallGuids.has(wallGuid)) return

      setWallGuidForOpening(wallGuid)
      unsetSelectionMode()
    },
    [unsetSelectionMode, wallGuids],
  )

  useModelClickListeners(
    handleWallSelect,
    [wallGuids, unsetSelectionMode],
    isSelectingWall,
    true,
    false,
    true,
  )

  const { mutate: parseAsOpening, isLoading: isParsingOpening } = useMutation({
    mutationFn: async () => {
      if (
        !projectId ||
        !ifcFileId ||
        !wallGuidForOpening.trim() ||
        ifcOpeningIds.length === 0 ||
        ifcOpeningIds.some(id => !id.trim())
      )
        return
      const configs = ifcOpeningIds
        .filter(id => id.trim() !== '')
        .map(ifc_element_id => ({
          ifc_element_id: ifc_element_id.trim(),
          ifc_file_id: ifcFileId,
          parsedWallId: wallGuidForOpening.trim(),
          limitOpeningXYCoords,
        }))
      return startIfcOpeningParse.request(projectId, configs) as Promise<
        IFCOpeningParseReport | undefined
      >
    },
    onSuccess: async (report?: IFCOpeningParseReport) => {
      if (projectId) await invalidateModel(projectId)
      setWallGuidForOpening('')
      const warning = getOpeningParseWarning(report)
      if (warning) enqueueSnackbar(warning, { variant: 'warning' })
      else
        enqueueSnackbar(
          `${ifcOpeningIds.length > 1 ? 'Öffnungen' : 'Öffnung'} erfolgreich importiert`,
          {
            variant: 'success',
          },
        )
      onSuccess()
    },
    onError: async (error: AxiosError) => {
      enqueueSnackbar(
        buildErrorMessage(error, 'Fehler beim Parsen der Öffnung. Bitte Modell kontrollieren.'),
        {
          variant: 'error',
        },
      )
      if (projectId) await invalidateModel(projectId)
    },
  })

  const canParseOpening = Boolean(
    projectId &&
      ifcFileId &&
      wallGuidForOpening.trim() !== '' &&
      ifcOpeningIds.length > 0 &&
      ifcOpeningIds.every(id => id.trim() !== ''),
  )

  return (
    <Stack spacing={1}>
      <FormControlLabel
        control={
          <Switch
            checked={limitOpeningXYCoords}
            onChange={e => setLimitOpeningXYCoords(e.target.checked)}
            disabled={isParsingOpening}
            size="small"
          />
        }
        label="Öffnungen auf 2 Dez. begrenzen (XY)"
      />
      <Typography variant="subtitle2" color="text.secondary">
        Öffnung in bestehende Wand parsen
      </Typography>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          p: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'grey.50',
        }}
      >
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {ifcOpeningIds.length > 1 ? 'Öffnungen' : 'Öffnung'}
          </Typography>
          <Typography
            variant="body2"
            noWrap
            color={ifcOpeningIds.length === 0 ? 'text.disabled' : 'text.primary'}
            title={ifcOpeningIds.join(', ')}
          >
            {ifcOpeningIds.length === 0
              ? 'Keine Öffnung gewählt'
              : ifcOpeningIds.length === 1
              ? ifcOpeningIds[0]
              : `${ifcOpeningIds.length} Öffnungen`}
          </Typography>
        </Stack>
        <ArrowForward fontSize="small" color="action" />
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            Zielwand
          </Typography>
          <Typography
            variant="body2"
            noWrap
            color={wallGuidForOpening ? 'text.primary' : 'text.disabled'}
            title={wallGuidForOpening}
          >
            {wallGuidForOpening || 'Keine Wand gewählt'}
          </Typography>
        </Stack>
      </Stack>
      <Button
        variant="outlined"
        size="small"
        onClick={() =>
          setSelectionMode({
            key: SELECT_WALL_FOR_OPENING_MODE_KEY,
            message: 'Klicken Sie eine Wand im Modell an, um die Zielwand zu setzen',
          })
        }
        disabled={isParsingOpening}
      >
        {wallGuidForOpening ? 'Neue Wand wählen' : 'Im Modell wählen'}
      </Button>

      <ImportButton
        variant="contained"
        onClick={() => parseAsOpening()}
        disabled={!canParseOpening}
        loading={isParsingOpening}
        size="small"
        fullWidth
      >
        {ifcOpeningIds.length > 1
          ? `${ifcOpeningIds.length} Öffnungen in Wand parsen`
          : 'Öffnung in Wand parsen'}
      </ImportButton>
    </Stack>
  )
}

export default IFCParseAsWallOpeningForm
